import React, { useEffect, useState } from 'react';
import { canItemBeSyncedInSalesChannel, showError, showSuccess, stringifyAndParseObj } from 'utils';
import styles from './index.module.scss';
import { fetchSalesChannelMappingDetails, removeInventoryItemFromSalesChannel, restoreInventoryItemToSalesChannel } from 'services/api/salesChannel';
import { Loading } from 'components/loading/Loading';
import RenderInfo from 'components/RenderInfo';
import _ from 'lodash';
import InventoryItem from './inventoryItem';
import BackButton from 'components/BackButton';
import { SyncItemButton } from '../details/inventoryItems';
import ActionButton from 'components/ActionButton';
import SyncHistory from './syncHistory';

const EditSalesChannelItem = (props) => {
  const { id } = props.match.params;

  const [loading, setLoading] = useState<boolean>(false);
  const [restoreMappingLoading, setRestoreMappingLoading] = useState<boolean>(false);
  const [deleteMappingLoading, setDeletedMappingLoading] = useState<boolean>(false);
  const [itemDetails, setItemDetails] = useState({}) as any;
  
  const fetchSalesChannelMappingDetailsFn = async () => {
    setLoading(true);
    try {
      let response = await fetchSalesChannelMappingDetails({ id });
      response = stringifyAndParseObj(response);
      setItemDetails(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSalesChannelMappingDetailsFn();
  }, []);

  const salesChannelId = _.get(itemDetails, 'salesChannel.objectId', '');
  const inventoryItemId = _.get(itemDetails, 'inventoryItem.objectId', '');

  const deleteInventoryItem = async () => {
    try {
      setDeletedMappingLoading(true);
      await removeInventoryItemFromSalesChannel({
        salesChannelId,
        inventoryItemId,
      });
      showSuccess('Mapping deleted successfully.');
      await fetchSalesChannelMappingDetailsFn();
    } catch (e) {
      showError(e);
    } finally {
      setDeletedMappingLoading(false);
    }
  };

  const restoreInventoryItem = async () => {
    try {
      setRestoreMappingLoading(true);
      await restoreInventoryItemToSalesChannel(itemDetails?.objectId);
      showSuccess('Item restored successfully.');
      await fetchSalesChannelMappingDetailsFn();
    } catch (e) {
      showError(e);
    } finally {
      setRestoreMappingLoading(false);
    }
  };

  const getActionButtons = () => {
    if (loading) return null;
    return (
      <div className='mb-2'>
        <SyncItemButton item={itemDetails} salesChannelId={salesChannelId} onSuccess={fetchSalesChannelMappingDetailsFn} />
        {itemDetails?.isDeleted ? 
          <ActionButton
            text={'Restore'}
            active={!restoreMappingLoading}
            isLoading={restoreMappingLoading}
            onClick={restoreInventoryItem}
            customClass='mt-2'
          /> : 
          <ActionButton
            text={'Delete'}
            active={!deleteMappingLoading}
            isLoading={deleteMappingLoading}
            onClick={deleteInventoryItem}
            customClass={`${styles['delete-button']} mt-2`}
          />
        }
      </div>
    );
  };

  const getSalesChannelDetails = () => {
    const canBeSynced = canItemBeSyncedInSalesChannel(itemDetails);

    return (
      <div className={styles['section-container']}>
        <div className={styles['heading']}>Basic Details</div>
        <div className='d-flex justify-content-between'>
          <div>
            <RenderInfo
              heading="Sales Channel Name"
              value={_.get(itemDetails, 'salesChannel.name', '-')}
            />
            <RenderInfo
              heading="Sales Channel Description"
              value={_.get(itemDetails, 'salesChannel.description', '-')}
            />
            <RenderInfo
              heading="Price"
              value={parseFloat(itemDetails?.inventoryItem?.price?.toFixed(2) || itemDetails?.price?.toFixed(2))}
              type='currency'
            />
            <RenderInfo
              heading="Quantity"
              value={itemDetails?.inventoryItem?.stock || itemDetails?.stock || '-'}
            />
            <RenderInfo
              heading="GroupId"
              value={_.get(itemDetails, 'inventoryItem.category.group.objectId', itemDetails?.category?.group?.objectId)}
            />
            <RenderInfo
              heading="GroupName"
              value={_.get(itemDetails, 'inventoryItemCategoryName', _.get(itemDetails, 'inventoryItem.category.group.name', '-'))}
            />
            <RenderInfo
              heading="CategoryId"
              value={_.get(itemDetails, 'inventoryItem.category.objectId', itemDetails?.category?.objectId)}
            />
            <RenderInfo
              heading="CategoryName"
              value={_.get(itemDetails, 'inventoryItemSubCategoryName', _.get(itemDetails, 'inventoryItem.category.description', '-'))}
            />
          </div>
          <div>
            {getActionButtons()}
            <RenderInfo
              heading="Deleted"
              value={itemDetails.isDeleted}
              type='boolean'
            />
            <RenderInfo
              heading="Last Sync Needed"
              value={itemDetails.lastSyncNeeded?.iso}
              type='date'
            />
            <RenderInfo
              heading="Last Sync Performed"
              value={itemDetails.lastSyncPerformed?.iso}
              type='date'
            />
            <RenderInfo
              heading="Sync Status"
              value={canBeSynced ? 'Pending Sync' : 'Synced'}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className={styles['main-container']}>
        <div className={styles['header']}>
          <div className={styles['heading']}>Sales Channel Item</div>
          <div className={styles['header-buttons']}>
            <BackButton />
          </div>
        </div>
        {loading && <Loading />}
        <div style={{ padding: '20px 15px' }}>
          {getSalesChannelDetails()}
          <InventoryItem
            itemDetails={itemDetails}
            setItemDetails={setItemDetails}
          />
          <SyncHistory loading={loading} syncHistory={itemDetails?.syncHistory} />
        </div>
      </div>
    </div>
  );
};

export default EditSalesChannelItem;
